/**
 * Left navigation. Shows a realtime pending badge next to "Approvals" and a
 * live green online-count badge next to "Live".
 */
import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { usePendingUsers, usePresence } from '../hooks/useFirestore';
import { useNow } from '../hooks/useNow';
import { isOnline } from '../lib/presence';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
}

const NAV: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: '▤', end: true },
  { to: '/live', label: 'Live', icon: '●' },
  { to: '/approvals', label: 'Approvals', icon: '✔' },
  { to: '/users', label: 'Users', icon: '☰' },
  { to: '/versions', label: 'App versions', icon: '⇪' },
  { to: '/insights', label: 'Insights', icon: '◆' },
  { to: '/activity', label: 'Activity report', icon: '▥' },
  { to: '/wishlists', label: 'Wishlists', icon: '♥' },
  { to: '/scratch', label: 'Scratch & Win', icon: '✦' },
  { to: '/notifications', label: 'Notifications', icon: '✉' },
  { to: '/sync', label: 'Sync Monitor', icon: '⟳' },
  { to: '/catalog', label: 'Catalog', icon: '▦' },
  { to: '/pdfs', label: 'PDFs', icon: '▤' },
];

export function Sidebar({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const pending = usePendingUsers();
  const pendingCount = pending.data.length;

  const presence = usePresence();
  const now = useNow(4000);
  const onlineCount = useMemo(
    () => presence.data.filter((p) => isOnline(p, now)).length,
    [presence.data, now],
  );

  return (
    <nav className="flex h-full w-60 flex-col border-r border-slate-200 bg-white">
      <div className="flex h-14 items-center gap-2 border-b border-slate-200 px-5">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-600 text-sm font-bold text-white">
          M
        </span>
        <div className="leading-tight">
          <p className="text-sm font-semibold text-slate-900">ANYWAY IT SOLUTIONS</p>
          <p className="text-[11px] text-slate-500">Admin Console</p>
        </div>
      </div>

      <ul className="flex-1 space-y-1 p-3">
        {NAV.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                [
                  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
                ].join(' ')
              }
            >
              <span aria-hidden className="w-4 text-center text-base opacity-70">
                {item.icon}
              </span>
              <span className="flex-1">{item.label}</span>
              {item.to === '/live' && onlineCount > 0 && (
                <span className="inline-flex min-w-[1.25rem] items-center justify-center gap-1 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                  <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-white" />
                  {onlineCount}
                </span>
              )}
              {item.to === '/approvals' && pendingCount > 0 && (
                <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[11px] font-semibold text-white">
                  {pendingCount}
                </span>
              )}
            </NavLink>
          </li>
        ))}
      </ul>

      <div className="border-t border-slate-200 p-3 text-[11px] text-slate-400">
        Prices are confidential. Read-only mirror of production data.
      </div>
    </nav>
  );
}
